import React, { useState, useEffect } from 'react';
import { Modal, Stack, TextInput, Button, Group } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';

interface Swimlane {
  _id: string;
  title: string;
}

interface SwimlaneEditModalProps {
  opened: boolean;
  onClose: () => void;
  swimlane?: Swimlane | null; // null для создания, объект для редактирования
  onSwimlaneCreated?: (swimlaneId: string, swimlaneData: Swimlane) => void;
  onSwimlaneUpdated?: (swimlaneId: string, swimlaneData: Swimlane) => void;
}

export const SwimlaneEditModal: React.FC<SwimlaneEditModalProps> = ({
  opened,
  onClose,
  swimlane,
  onSwimlaneCreated,
  onSwimlaneUpdated,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
  });

  const isEditMode = !!swimlane;

  // Инициализация формы при открытии модалки
  useEffect(() => {
    if (opened) {
      if (swimlane) {
        // Режим редактирования - заполняем форму данными свимлейна
        setFormData({
          title: swimlane.title,
        });
      } else {
        // Режим создания - очищаем форму
        setFormData({
          title: '',
        });
      }
    }
  }, [opened, swimlane]);

  const createSwimlaneMutation = useMutation({
    mutationFn: async (data: typeof formData) => api('/swimlanes', { method: 'POST', body: data }),
    onSuccess: (data) => {
      showToast({
        title: t('messages.created', { item: t('swimlanes.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['swimlanes'] });
      if (onSwimlaneCreated) {
        onSwimlaneCreated(data._id, data);
      }
      handleClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorCreating', { item: t('swimlanes.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const updateSwimlaneMutation = useMutation({
    mutationFn: async (data: typeof formData) =>
      api(`/swimlanes/${swimlane?._id}`, { method: 'PATCH', body: data }),
    onSuccess: (data) => {
      showToast({
        title: t('messages.updated', { item: t('swimlanes.title') }),
        message: '',
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['swimlanes'] });
      if (onSwimlaneUpdated) {
        onSwimlaneUpdated(data._id, data);
      }
      handleClose();
    },
    onError: (error) => {
      const message = getErrorMessage(
        error,
        t('messages.errorUpdating', { item: t('swimlanes.title') }),
      );
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleClose = () => {
    setFormData({ title: '' });
    onClose();
  };

  const handleSave = () => {
    if (!formData.title.trim()) {
      showToast({ title: t('common.error'), message: t('messages.validationError'), color: 'red' });
      return;
    }

    if (isEditMode) {
      updateSwimlaneMutation.mutate(formData);
    } else {
      createSwimlaneMutation.mutate(formData);
    }
  };

  const handleKeyDown = createEnterKeyHandler({ onSubmit: handleSave });

  const isLoading = createSwimlaneMutation.isPending || updateSwimlaneMutation.isPending;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditMode ? t('swimlanes.editSwimlane') : t('swimlanes.addSwimlane')}
      size="md"
    >
      <Stack>
        <TextInput
          label={t('swimlanes.name')}
          placeholder={t('swimlanes.name')}
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          autoFocus
          onKeyDown={handleKeyDown}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose} color="gray">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} color="teal" loading={isLoading}>
            {isEditMode ? t('common.save') : t('common.add')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
